/* eslint-disable */
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getContext, useCombinedRefs } from 'common';
import MoreButton from 'components/MoreButton';
import HorizontalMoreButton, { HorizontalMoreButtonItem, HorizontalMoreButtonItemIcon } from 'components/HorizontalMoreButton';
import MoreVerticalIcon from '@nokia-csf-uxr/ccfk-assets/MoreVerticalIcon';
import Styled from './styles';
import { CONSTANTS } from '../../GridHelpers';
import {
  handleWrapperFocus,
  isSelectionKeyPressed,
  handleMenuKeyDown,
} from './utils/RowActionsKeyboardNav';

const { ROW_ACTION_COLID } = CONSTANTS;
const SMALL_BUTTON_SIZE = '1.75rem';
const SMALL_BUTTON_SIZE_PX = 28;


/** Provides actions in an overflow menu in the style of a common menu as well as actions with just icons inline.
 * To re-positon menu on changes to children or target element, please use `positionMenu` callback using component ref.
 * The forwarded ref returns reference to the row actions wrapper.
 * To pass props to the root element make use of the 'wrapperProps' prop.
*/
const RowActions = React.forwardRef((props, ref) => getContext(({ RTL }) => {
  const {
    wrapperProps,
    moreButtonProps,
    horizontalMoreButtonProps,
    getRowActions,
    getInlineRowActions,
    getProgressIndicator,
    isDynamic,
    alwaysShow,
    node,
    getNextFocusableElAfterGrid,
    handleTabOnMenu,
    ...otherProps
  } = props;
  const {
    onClose,
    onOpen,
    ...otherMoreButtonProps
  } = moreButtonProps;
  const {
    onClose: horizMenuOnClose,
    onOpen: horizMenuOnOpen,
    ...otherHorizontalMoreButtonProps
  } = horizontalMoreButtonProps;

  const moreButtonRef = useRef(null);
  const horizontalMenuRef = useRef(null);
  const rowActionsRef = useRef(null);
  const combinedRowActionsRef = useCombinedRefs(ref, rowActionsRef);
  const [triggerEl, setTriggerEl] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showHorizontalMenu, setShowHorizontalMenu] = useState(false);
  const [listLoaded, setListLoaded] = useState(false);
  const [dynamicActions, setDynamicActions] = useState(null);
  let actions;
  let inlineActions;
  const isRowActionsDynamic = isDynamic({ ...otherProps });

  const openHorizMenu = (focusCellForHover = false) => {
    if (!showMenu && !props.api.context.lastCell) {
      setShowHorizontalMenu(true);
      horizMenuOnOpen && horizMenuOnOpen();
      // To highlight row on row action menu hover, focus row action column
      if (focusCellForHover) {
        // eslint-disable-next-line no-param-reassign
        props.api.context.focusCellForHover = true;
        props.api.setFocusedCell(node.rowIndex, ROW_ACTION_COLID);
      }
    }
  };

  const closeHorizMenu = () => {
    setShowHorizontalMenu(false);
    horizMenuOnClose && horizMenuOnClose();
    // eslint-disable-next-line no-param-reassign
    props.api.context.focusCellForHover = false;
  };

  const openMenu = () => {
    setShowMenu(true);
    onOpen && onOpen();
  };

  const closeMenu = () => {
    setShowMenu(false);
    onClose && onClose();
  };

  /** Function to populate dynamic row actions in the drop down menu. */
  const loadDynamicActions = () => {
    new Promise((resolve) => {
      // this is a callback to updateActions
      const updateActions = (results) => {
        resolve(results);
      };
      getRowActions({ ...otherProps, compactMode: props.api.context.compactMode }, updateActions);
      setTimeout(() => {
        // if delay is more than 10 seconds this closes the progressIndicator and shows static menu
        if (!listLoaded) {
          setListLoaded(true);
        }
      }, 10000);
    }).then((results) => {
      if (results) {
        setDynamicActions(results);
        setListLoaded(true);
      }
    });
  };

  if (!isRowActionsDynamic && getRowActions) {
    actions = getRowActions({ ...otherProps, compactMode: props.api.context.compactMode });
  }

  if (getInlineRowActions) {
    inlineActions = getInlineRowActions({ ...otherProps, compactMode: props.api.context.compactMode });
    // if there are row actions, add a menu button to inline row actions.
    if (inlineActions && getRowActions) {
      const moreButtonItem = (
        <HorizontalMoreButtonItem
          onClick={() => {
            openMenu();
            closeHorizMenu();
            isRowActionsDynamic && loadDynamicActions();
          }}
          onKeyDown={(event) => {
            if (isSelectionKeyPressed(event.key)) {
              openMenu();
              closeHorizMenu();
              isRowActionsDynamic && loadDynamicActions();
            }
          }}
          aria-label="More"
          key={`more-${node.rowIndex}`}
        >
          <HorizontalMoreButtonItemIcon
            style={props.api.context.compactMode ? { width: SMALL_BUTTON_SIZE, height: SMALL_BUTTON_SIZE } : {}}
          ><MoreVerticalIcon />
          </HorizontalMoreButtonItemIcon>
        </HorizontalMoreButtonItem>
      );
      // To have the more button as part of the menu create a more button menu item.
      !RTL && inlineActions.push(moreButtonItem);
      RTL && inlineActions.splice(0, 0, moreButtonItem);
    }
  }

  useEffect(() => {
    moreButtonRef.current && setTriggerEl(moreButtonRef.current.buttonRef);
  }, [moreButtonRef.current]);

  // Re-position menu after dynamic actions are loaded as the menu size has changed.
  useEffect(() => {
    if (dynamicActions) {
      moreButtonRef.current && moreButtonRef.current.positionMenu();
    }
  }, [dynamicActions]);

  // Trigger element loses focus on state change hence re-focus trigger element while showing the dropdown menu.
  // Doesn't have to be focused for horizontal menu as the menu covers the trigger element.
  useEffect(() => {
    if (showMenu) {
      triggerEl && triggerEl.current && triggerEl.current.focus();
    }
  }, [showHorizontalMenu, showMenu]);

  if (!actions && !inlineActions) {
    return null;
  }

  return (
    <Styled.RowActionsWrapper
      ref={combinedRowActionsRef}
      alwaysShow={alwaysShow}
      isMenuOpen={showHorizontalMenu || showMenu}
      tabIndex="0"
      onFocus={event => handleWrapperFocus(event, rowActionsRef)}
      {...wrapperProps}
    >
      {actions && !actions.props.children.length && actions}
      {(!actions || actions.props.children.length) && (
        <MoreButton
          isOpen={showMenu}
          ref={moreButtonRef}
          style={props.api.context.compactMode ? { width: SMALL_BUTTON_SIZE, height: SMALL_BUTTON_SIZE } : {}}
          listProps={{
            onKeyDown: event => handleMenuKeyDown(event, props.columnApi.getAllGridColumns(), node, props.column.colId, props.api, triggerEl, getNextFocusableElAfterGrid, handleTabOnMenu),
            keyboardFocus: false
          }}
          onOpen={() => {
            openMenu();
            inlineActions && closeHorizMenu();
            isRowActionsDynamic && loadDynamicActions();
          }}
          onClose={() => {
            closeMenu();
            inlineActions && openHorizMenu();
            isRowActionsDynamic && setDynamicActions(null);
            isRowActionsDynamic && setListLoaded(false);
          }}
          onMouseOver={() => {
            inlineActions && openHorizMenu(true);
          }}
          onMouseOut={(event) => {
            if (horizontalMenuRef.current && horizontalMenuRef.current.menuRef && horizontalMenuRef.current.menuRef.menuRef && !horizontalMenuRef.current.menuRef.menuRef.contains(event.relatedTarget)) {
              closeHorizMenu();
            }
          }}
          onFocus={() => {
            inlineActions && openHorizMenu();
          }}
          onBlur={(event) => {
            if (horizontalMenuRef.current && horizontalMenuRef.current.menuRef && horizontalMenuRef.current.menuRef.menuRef && !horizontalMenuRef.current.menuRef.menuRef.contains(event.relatedTarget)) {
              closeHorizMenu();
            }
            if (moreButtonRef.current.menuRef && moreButtonRef.current.menuRef.menuRef && !moreButtonRef.current.menuRef.menuRef.contains(event.relatedTarget)) {
              closeMenu();
              isRowActionsDynamic && setDynamicActions(null);
              isRowActionsDynamic && setListLoaded(false);
            }
          }}
          {...otherMoreButtonProps}
        >
          {!!actions && actions}
          {!!dynamicActions && dynamicActions}
          {isRowActionsDynamic && !listLoaded && getProgressIndicator()}
        </MoreButton>)}
      {inlineActions && triggerEl && (
        <HorizontalMoreButton
          ref={horizontalMenuRef}
          triggerEl={triggerEl}
          isOpen={showHorizontalMenu}
          menuProps={props.api.context.compactMode ? {
            positioningOptions: {
              placement: RTL ? 'right' : 'left',
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [props.api.context.compactMode ? 2 : 0, `-${SMALL_BUTTON_SIZE_PX}`]
                  }
                }
              ]
            }
          } : {}}
          listProps={{
            style: props.api.context.compactMode ? { minHeight: SMALL_BUTTON_SIZE, padding: 0 } : undefined,
            onKeyDown: event => handleMenuKeyDown(event, props.columnApi.getAllGridColumns(), node, props.column.colId, props.api, triggerEl, getNextFocusableElAfterGrid, handleTabOnMenu),
            keyboardFocus: false.valueOf,
            onClick: e => e.preventDefault()
          }}
          onClose={closeHorizMenu}
          {...otherHorizontalMoreButtonProps}
        >
          {inlineActions}
        </HorizontalMoreButton>
      )}
    </Styled.RowActionsWrapper>
  );
}));

RowActions.displayName = 'RowActions';

RowActions.propTypes = {
  /** Row node asscoiated to the row actions. */
  node: PropTypes.shape({}).isRequired,
  /** Forwards props to the container element (root element). */
  wrapperProps: PropTypes.shape({}),
  horizontalMoreButtonProps: PropTypes.shape({
    /** Override `Menu` props used for positioning actions menu with respect to icon button. */
    menuProps: PropTypes.shape({
      /** Have `menu` render in portal. */
      usePortal: PropTypes.bool,
      /** Specify container in which `menu` is rendered in portal. */
      portalContainer: PropTypes.oneOfType([PropTypes.element, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
      /** Override `menu` positioning options based on popper (https://popper.js.org/docs/v2/). */
      positioningOptions: PropTypes.shape({})
    }),
    /** Override `List` props that act as a container for the menu items inside menu. */
    listProps: PropTypes.shape({}),
    /**
     * Callback fired when the component requests to be opened.
     *
     * @param {object} event The event source of the callback.
     */
    onOpen: PropTypes.func,
    /**
     * Callback fired when the component requests to be closed.
     *
     * @param {object} event The event source of the callback.
     */
    onClose: PropTypes.func
  }),
  moreButtonProps: PropTypes.shape({
    /** Override `Menu` props used for positioning actions menu with respect to icon button. */
    menuProps: PropTypes.shape({
      /** Have `menu` render in portal. */
      usePortal: PropTypes.bool,
      /** Specify container in which `menu` is rendered in portal. */
      portalContainer: PropTypes.oneOfType([PropTypes.element, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
      /** Override `menu` positioning options based on popper (https://popper.js.org/docs/v2/). */
      positioningOptions: PropTypes.shape({})
    }),
    /** Override `List` props that act as a container for the menu items inside menu. */
    listProps: PropTypes.shape({}),
    /**
     * Callback fired when the component requests to be opened.
     *
     * @param {object} event The event source of the callback.
     */
    onOpen: PropTypes.func,
    /**
     * Callback fired when the component requests to be closed.
     *
     * @param {object} event The event source of the callback.
     */
    onClose: PropTypes.func
  }),
  /** To render individual actions or drop down menu items, consider using 'MoreButtonItem' or 'IconButton'. */
  getRowActions: PropTypes.func,
  /** To render the inline menu items, consider using 'HorizontalMoreButtonItem'. */
  getInlineRowActions: PropTypes.func,
  /** To render progress indicator in case of dynamic actions, consider using 'MoreButtonItemProgressIndicator'. */
  getProgressIndicator: PropTypes.func,
  /** Determine whether row actions are dynamic. */
  isDynamic: PropTypes.func,
  /** Determine whether to always show row actions. */
  alwaysShow: PropTypes.bool,
  /** To get reference to the next focusable element after grid which will be used to apply focus on exiting the grid. */
  getNextFocusableElAfterGrid: PropTypes.func,
  /** Use to handle 'Tab' on menu especially if you want to focus inside an expanded row */
  handleTabOnMenu: PropTypes.func
};

RowActions.defaultProps = {
  wrapperProps: {},
  horizontalMoreButtonProps: {},
  moreButtonProps: {},
  getRowActions: undefined,
  getInlineRowActions: undefined,
  getProgressIndicator: undefined,
  isDynamic: () => false,
  alwaysShow: false,
  getNextFocusableElAfterGrid: undefined,
  handleTabOnMenu: undefined
};

export default RowActions;